import Dispatcher, { IAction, IActionPayload } from '../Dispatcher'
import { load, save } from '../../repositories/BoardsRepository'
import BoardStore, { IBoardBase, isIBoardBase } from './BoardStore'
import * as evaluationData from '../../testdata/BoardStore.json'

// Types associated with Board Actions
const ADD_BOARD = 'ADD_BOARD'
const UPDATE_BOARD = 'UPDATE_BOARD'
const DELETE_BOARD = 'DELETE_BOARD'
const BOARDS_LOADED = 'BOARDS_LOADED'
const BOARDS_SAVED = 'BOARDS_SAVED'
const CREATE_BOARD = 'CREATE_BOARD'
const MOVE_BOARD = 'MOVE_BOARD'

/**
 * Represents payload associated with moving the position
 * of a board within the board store
 */
export interface IBoardMovePayload {
    fromBoardId: number
    toBoardId: number
}

/**
 * Typeguard for IAction<IMoveBoardPayload>
 * Checks if action passed as a parameter has an IMoveBoardPayload.
 * If true then action is IMoveBoardPayload.
 * If false then is treated as IAction
 * @param action Action to check
 */
function isBoardMoveAction(
    action: IAction | IActionPayload<IBoardMovePayload>
): action is IActionPayload<IBoardMovePayload> {
    const payload = action as IActionPayload<IBoardMovePayload>
    // ensure we have a payload
    if (!payload.payload) return false
    // ensure we have a fromBoardId
    if (payload.payload.fromBoardId === undefined) return false
    // ensure we have a toBoardId
    if (payload.payload.toBoardId === undefined) return false
    return true
}

/**
 * Typeguard for IBoard Action.
 * Returns undefined if the action passed in is not a IBoardAction.
 * Otherwise will return true.
 * @param action Action to check
 */
function isBoardAction(
    action: IAction | IActionPayload<IBoardBase>
): action is IActionPayload<IBoardBase> {
    const value = action as IActionPayload<IBoardBase>
    // if no payload then false
    if (!value.payload) return false
    // ensure payload is IBoard
    return isIBoardBase(value.payload)
}

/**
 * dispatches action to add a new board to the board store
 * @param board Board to add to store
 */
function addBoard(board: IBoardBase) {
    Dispatcher.dispatch({
        actionType: ADD_BOARD,
        payload: board,
    } as IActionPayload<IBoardBase>)
}

/**
 * dispatches action to delete a board from the board store
 * @param board Board to delete from store
 */
function deleteBoard(board: IBoardBase) {
    Dispatcher.dispatch({
        actionType: DELETE_BOARD,
        payload: board,
    } as IActionPayload<IBoardBase>)
}

/**
 * dispatches action to update a board in the board store
 * @param board Board to update in store
 */
function updateBoard(board: IBoardBase) {
    Dispatcher.dispatch({
        actionType: UPDATE_BOARD,
        payload: board,
    } as IActionPayload<IBoardBase>)
}

/**
 * Fetches the boards from the board repository.
 * dispatches board loaded action with boards to populate the board store
 * @param {boolean} loadEvaludationData if true loads eval data else loads real data
 */
function loadBoards(loadEvaludationData: boolean) {
    Dispatcher.dispatch({
        actionType: BOARDS_LOADED,
        payload: loadEvaludationData ? evaluationData.data : load(),
    } as IActionPayload<{ [key: number]: IBoardBase }>)
}

/**
 * Saves the board state to the repository
 */
function saveBoards() {
    save(BoardStore.boards)
    Dispatcher.dispatch({
        actionType: BOARDS_SAVED,
    })
}

/**
 * dispatches action to inform application
 * the user wants to create a new board
 */
function createBoard() {
    Dispatcher.dispatch({
        actionType: CREATE_BOARD,
    })
}

/**
 * dispatches action to change a boards order
 * within the board store
 * @param fromBoardId id of board to be moved
 * @param toBoardId id of board to move from board to
 */
function moveBoard(fromBoardId: number, toBoardId: number) {
    Dispatcher.dispatch({
        actionType: MOVE_BOARD,
        payload: {
            fromBoardId,
            toBoardId,
        },
    } as IActionPayload<IBoardMovePayload>)
}

export {
    addBoard,
    deleteBoard,
    updateBoard,
    loadBoards,
    saveBoards,
    createBoard,
    isBoardAction,
    moveBoard,
    ADD_BOARD,
    UPDATE_BOARD,
    DELETE_BOARD,
    BOARDS_LOADED,
    CREATE_BOARD,
    BOARDS_SAVED,
    MOVE_BOARD,
    isBoardMoveAction,
}
